import { HTTP_HYPER_ESPACE_BACKEND } from '@/services/http-common'
import { buildUriForCall } from '@/helpers/functions/buildUri'
import { Message } from 'element-ui'

export default {
  namespaced: true,
  state: {
    loadingCurrentPost: false,
    currentPost: null,
    loadingDiffusions: false,
    socialPosts: {
      Facebook: { status: 'no planned', content: null, date: null },
      Instagram: { status: 'no planned', content: null, date: null },
      Google: { status: 'no planned', content: null, date: null },
      Linkedin: { status: 'no planned', content: null, date: null }
    },
    statusMediasPost: { hasPhotos: false, hasVideoUrl: false, hasVideoUpload: false },
    uploadedVideoPath: null,
    analyzingMedias: false, // Protection contre les appels multiples
    lastAnalyzedPostId: null, // Éviter d'analyser le même post plusieurs fois
    temporaryVideosCache: null, // Cache pour éviter les appels multiples à /videos
    temporaryVideosCacheTime: null // Timestamp du cache
  },
  getters: {
    getCurrentPost: (state) => state.currentPost,
    isBienVendu: (state) => state.currentPost ? state.currentPost.etat === 'V' : false,
    universe: (state) => state.currentPost ? state.currentPost.univers : null,
    getSocialStatusPosts: (state) => state.socialPosts,
    getVideoUrlsByNetwork: (state) => {
      if (!state.currentPost ||
          !state.currentPost.donnees ||
          !state.currentPost.donnees.post_video_file_url_custom ||
          !state.currentPost.donnees.post_video_file_url_custom.valeur) {
        return {}
      }
      return state.currentPost.donnees.post_video_file_url_custom.valeur
    },
    getStatusMediaPost: (state) => state.statusMediasPost,
    getUploadedVideoPath: (state) => state.uploadedVideoPath,

    // Nouveau getter dynamique pour le type de média prioritaire
    getMediaType: (state) => {
      // Priorité 1: Vidéo uploadée (dans le post ou temporaire)
      if (state.statusMediasPost.hasVideoUpload) {
        return 'upload'
      }
      // Priorité 2: URL vidéo
      if (state.statusMediasPost.hasVideoUrl) {
        return 'url'
      }
      // Priorité 3: Photos (par défaut)
      return 'image'
    },

    // Getter pour savoir si on a des médias
    hasAnyMedia: (state) => {
      return state.statusMediasPost.hasPhotos ||
             state.statusMediasPost.hasVideoUrl ||
             state.statusMediasPost.hasVideoUpload
    }
  },
  mutations: {
    setLoadingCurrentPost (state, loadingCurrentPost) {
      state.loadingCurrentPost = loadingCurrentPost
    },
    setLoadingDiffusions (state, loadingDiffusions) {
      state.loadingDiffusions = loadingDiffusions
    },
    setCurrentPost (state, currentPost) {
      state.currentPost = currentPost
      // Reset des flags d'analyse quand on change de post
      state.lastAnalyzedPostId = null
      state.analyzingMedias = false
      // Invalider le cache des vidéos temporaires pour forcer un rechargement
      state.temporaryVideosCache = null
      state.temporaryVideosCacheTime = null
    },
    updatePostVideoFileUrlCustom (state, { network, url }) {
      // console.log(network, url)
      const networks = ['Facebook', 'Instagram', 'Google', 'Linkedin']

      // Fonction de validation URL (même logique que dans PostManager.vue)
      const urlIsValid = (urlString) => {
        if (!urlString || urlString.trim() === '') return true // URL vide est valide
        let isValid = false
        try {
          const urlVideo = new URL(urlString)
          isValid = (
            ((urlVideo.protocol === 'http:') || (urlVideo.protocol === 'https:')) &&
            (urlVideo.hostname.indexOf('youtube') === -1) &&
            (urlVideo.hostname.indexOf('dailymotion') === -1)
          )
        } catch (_) {
          isValid = false
        }
        return isValid
      }

      if (state.currentPost && state.currentPost.donnees) {
        if (!state.currentPost.donnees.post_video_file_url_custom) {
          state.currentPost.donnees.post_video_file_url_custom = {
            valeur: {}
          }
        }
        if (!state.currentPost.donnees.post_video_file_url_custom.valeur) {
          state.currentPost.donnees.post_video_file_url_custom.valeur = {}
        }

        const networkKey = network.toLowerCase()
        const urlValues = state.currentPost.donnees.post_video_file_url_custom.valeur

        // Si c'est l'URL initiale, TOUJOURS propager
        if (network === 'initial') {
          // Capturer l'ancienne URL initiale AVANT de la modifier
          const oldInitialUrl = urlValues.initial

          // Mettre à jour l'URL initiale d'abord
          urlValues[networkKey] = url

          // PROPAGATION SYSTÉMATIQUE : Pour chaque réseau, vérifier s'il doit être mis à jour
          for (const rs of networks) {
            const rsKey = rs.toLowerCase()
            const currentRsUrl = urlValues[rsKey]

            // Propager dans TOUS ces cas :
            // 1. Le réseau n'a pas d'URL (undefined ou null)
            // 2. Le réseau a une URL vide ('')
            // 3. Le réseau utilise l'ancienne URL initiale
            // 4. Le réseau utilise la même URL que celle qu'on tape (pour forcer la réactivité)
            const shouldPropagate = (
              !currentRsUrl || // Pas d'URL
              currentRsUrl === '' || // URL vide
              currentRsUrl === oldInitialUrl || // Utilise l'ancienne URL initiale
              currentRsUrl === url // Utilise déjà la nouvelle URL (pour forcer la réactivité)
            )

            if (shouldPropagate) {
              urlValues[rsKey] = url
            }
          }

          // Mettre à jour le statut des médias
          if (url === '') {
            state.statusMediasPost.hasVideoUrl = false
          } else if (urlIsValid(url)) {
            state.statusMediasPost.hasVideoUrl = true
          }
        }
        // Pour les autres réseaux
        else if (network !== 'initial') {
          urlValues[networkKey] = url
        }
      }
    },
    deletePostVideoFileUrlCustom (state, networkKey) {
      if (networkKey && state.currentPost &&
          state.currentPost.donnees &&
          state.currentPost.donnees.post_video_file_url_custom &&
          state.currentPost.donnees.post_video_file_url_custom.valeur) {
        const key = networkKey.toLowerCase()
        delete state.currentPost.donnees.post_video_file_url_custom.valeur[key]

        // Si plus aucune URL du tout, supprimer complètement la structure
        if (Object.keys(state.currentPost.donnees.post_video_file_url_custom.valeur).length === 0) {
          delete state.currentPost.donnees.post_video_file_url_custom
        }
      }
      state.statusMediasPost.hasVideoUpload = false
      state.statusMediasPost.hasVideoUrl = false
    },
    updateCurrentPostData (state, updatedPost) {
      if (state.currentPost && updatedPost) {
        Object.assign(state.currentPost, updatedPost)
      }
    },
    setSocialPosts (state, socialPosts) {
      state.socialPosts = socialPosts
    },
    cleanSocialPosts (state) {
      state.socialPosts = {
        Facebook: { status: 'no planned', content: null, date: null },
        Instagram: { status: 'no planned', content: null, date: null },
        Google: { status: 'no planned', content: null, date: null },
        Linkedin: { status: 'no planned', content: null, date: null }
      }
    },
    setStatusMediasPost (state, { key, value }) {
      state.statusMediasPost[key] = value
    },
    setUploadedVideoPath (state, path) {
      state.uploadedVideoPath = path
    },
    clearUploadedVideoPath (state) {
      state.uploadedVideoPath = null
    },
    setAnalyzingMedias (state, analyzing) {
      state.analyzingMedias = analyzing
    },
    setLastAnalyzedPostId (state, postId) {
      state.lastAnalyzedPostId = postId
    },
    setTemporaryVideosCache (state, { videos, timestamp }) {
      state.temporaryVideosCache = videos
      state.temporaryVideosCacheTime = timestamp
    },
    clearTemporaryVideosCache (state) {
      state.temporaryVideosCache = null
      state.temporaryVideosCacheTime = null
    }
  },
  actions: {
    // Action pour vérifier les vidéos temporaires avec cache
    async checkTemporaryVideos ({ commit, state }) {
      const now = Date.now()
      const cacheValidityDuration = 30000 // 30 secondes de cache

      // Vérifier si le cache est encore valide
      if (state.temporaryVideosCache !== null &&
          state.temporaryVideosCacheTime &&
          (now - state.temporaryVideosCacheTime) < cacheValidityDuration) {
        return state.temporaryVideosCache
      }

      try {
        const response = await HTTP_HYPER_ESPACE_BACKEND.get('/videos')
        const videosData = {
          count: response.data.count,
          videos: response.data.videos || []
        }

        // Mettre en cache le résultat
        commit('setTemporaryVideosCache', {
          videos: videosData,
          timestamp: now
        })

        return videosData
      } catch (error) {
        // Mettre en cache un résultat vide
        const emptyResult = { count: 0, videos: [] }
        commit('setTemporaryVideosCache', {
          videos: emptyResult,
          timestamp: now
        })

        return emptyResult
      }
    },

    // Nouvelle action centralisée pour analyser tous les médias
    async analyzePostMedias ({ commit, state, dispatch }, postId) {
      // Protection contre les appels multiples
      if (state.analyzingMedias) {
        return state.statusMediasPost
      }

      // Protection contre l'analyse répétée du même post
      if (state.lastAnalyzedPostId === postId) {
        return state.statusMediasPost
      }

      commit('setAnalyzingMedias', true)

      try {
        // Reset des statuts
        commit('setStatusMediasPost', { key: 'hasPhotos', value: false })
        commit('setStatusMediasPost', { key: 'hasVideoUrl', value: false })
        commit('setStatusMediasPost', { key: 'hasVideoUpload', value: false })

        const currentPost = state.currentPost
        if (!currentPost || currentPost.id !== postId) {
          console.warn('Post non chargé ou ID différent')
          return state.statusMediasPost
        }

        // 1. Vérifier les photos
        const hasPhotos = currentPost.photos && currentPost.photos.length > 0
        if (hasPhotos) {
          commit('setStatusMediasPost', { key: 'hasPhotos', value: true })
        }

        // 2. Vérifier les URLs vidéo
        let hasVideoUrl = false
        if (currentPost.donnees &&
            currentPost.donnees.post_video_file_url_custom &&
            currentPost.donnees.post_video_file_url_custom.valeur) {
          const urlValues = currentPost.donnees.post_video_file_url_custom.valeur
          // Vérifier s'il y a au moins une URL non vide
          hasVideoUrl = Object.values(urlValues).some(url => url && url.trim() !== '')
        }

        if (hasVideoUrl) {
          commit('setStatusMediasPost', { key: 'hasVideoUrl', value: true })
        }

        // 3. Vérifier les vidéos uploadées dans le post
        const hasVideoInPost = currentPost.assets &&
            currentPost.assets.VIDEO &&
            Object.values(currentPost.assets.VIDEO).length > 0

        // 4. Vérifier les vidéos temporaires uploadées (avec cache)
        const temporaryVideosData = await dispatch('checkTemporaryVideos')
        const hasTemporaryVideo = temporaryVideosData.count > 0

        if (hasTemporaryVideo) {
          const video = temporaryVideosData.videos[0]
          commit('setUploadedVideoPath', video.path)
        } else {
          commit('clearUploadedVideoPath')
        }

        // Mettre à jour le statut vidéo upload (post ou temporaire)
        if (hasVideoInPost || hasTemporaryVideo) {
          commit('setStatusMediasPost', { key: 'hasVideoUpload', value: true })
        }

        return state.statusMediasPost
      } catch (error) {
        return state.statusMediasPost
      } finally {
        commit('setAnalyzingMedias', false)
        commit('setLastAnalyzedPostId', postId)
      }
    },

    loadPost ({ commit }, postId) {
      commit('setLoadingCurrentPost', true)
      // Reset des flags d'analyse au début du chargement
      commit('setAnalyzingMedias', false)
      commit('setLastAnalyzedPostId', null)
      return HTTP_HYPER_ESPACE_BACKEND.get(`/annonce/${postId}`)
        .then((response) => {
          if (response.status === 200) {
            commit('setCurrentPost', response.data)
            return response.data
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          commit('setLoadingCurrentPost', false)
        })
    },
    loadDiffusionAnnonce ({ commit }, { postId, isBienVendu, isNotEDITO }) {
      commit('setLoadingDiffusions', true)
      // const url = '/diffuseurs'
      const url = '/v2/diffuseurs'
      return HTTP_HYPER_ESPACE_BACKEND.get(url)
        .then((response) => {
          const diffuseurs = response.data
          const filteredDiffuseurs = {}

          for (const [id, code] of Object.entries(diffuseurs)) {
            if (code.includes('SOCIAL_MANAGER')) {
              continue
            }

            let shouldInclude = false
            if (isBienVendu) {
              shouldInclude = code.endsWith('BIENS_VENDUS')
            } else if (isNotEDITO) {
              shouldInclude = code.includes('EDITO')
            } else {
              shouldInclude = !code.endsWith('BIENS_VENDUS') && !code.includes('EDITO')
            }

            if (shouldInclude) {
              filteredDiffuseurs[id] = code
            }
          }

          const params = {
            annonceId: postId,
            portalCodesAnnonces: Object.values(filteredDiffuseurs).join(','),
            linkedPortals: Object.keys(filteredDiffuseurs).map(Number),
            champsRechercheLibre: ['titre', 'reference'],
            rechercheLibre: '',
            page: 1
          }

          return HTTP_HYPER_ESPACE_BACKEND.get(buildUriForCall('/social-management-posts', params))
        })
        .then((response) => {
          const socialPosts = {
            Facebook: { status: 'no planned', content: null, date: null },
            Instagram: { status: 'no planned', content: null, date: null },
            Google: { status: 'no planned', content: null, date: null },
            Linkedin: { status: 'no planned', content: null, date: null }
          }

          const annonce = response.data.annonces[0]
          if (!annonce) {
            commit('setSocialPosts', socialPosts)
            return
          }

          const networks = ['Facebook', 'Instagram', 'Google', 'Linkedin']
          for (const network of networks) {
            const publishedDiffusion = annonce.diffusions.find(diffusion => {
              return diffusion.diffuseurName.toLowerCase().includes(network.toLowerCase()) &&
                     diffusion.isCurrenltyPublished &&
                     diffusion.firstSentAt
            })

            if (publishedDiffusion) {
              socialPosts[network] = {
                status: 'published',
                content: publishedDiffusion,
                date: publishedDiffusion.firstSentAt
              }
              continue
            }

            if (annonce.planifications && Array.isArray(annonce.planifications)) {
              const scheduledPlanification = annonce.planifications.find(planification => {
                return planification.scheduled &&
                       planification.scheduled.lastExecution === null &&
                       planification.portails.some(portail =>
                         portail.code.toLowerCase().includes(network.toLowerCase())
                       )
              })

              if (scheduledPlanification) {
                socialPosts[network] = {
                  status: 'scheduled',
                  content: scheduledPlanification,
                  date: scheduledPlanification.scheduled.estimatedDateExecution
                }
              }
            }

            if (annonce.planifications && Array.isArray(annonce.planifications)) {
              const scheduledPlanification = annonce.planifications.find(planification => {
                return planification.scheduled &&
                    planification.scheduled.lastExecution !== null &&
                    planification.portails.some(portail =>
                      portail.code.toLowerCase().includes(network.toLowerCase())
                    )
              })

              if (scheduledPlanification) {
                socialPosts[network] = {
                  status: 'published',
                  content: scheduledPlanification,
                  date: scheduledPlanification.scheduled.estimatedDateExecution
                }
              }
            }
          }

          commit('setSocialPosts', socialPosts)
        })
        .catch((error) => {
          console.log(error)
          Message.error('Erreur lors du chargement des diffusions et des planifications de l\'annonce')
        })
        .finally(() => {
          commit('setLoadingDiffusions', false)
        })
    },

    // Action pour invalider le cache des vidéos temporaires
    invalidateTemporaryVideosCache ({ commit }) {
      commit('clearTemporaryVideosCache')
      // Aussi réinitialiser le flag d'analyse pour permettre une nouvelle analyse
      commit('setLastAnalyzedPostId', null)
    },

    // Action utilitaire pour déclencher manuellement l'analyse des médias
    async refreshMediaAnalysis ({ dispatch, state }) {
      if (state.currentPost && state.currentPost.id) {
        // Invalider le cache avant l'analyse
        dispatch('invalidateTemporaryVideosCache')
        return await dispatch('analyzePostMedias', state.currentPost.id)
      }
      return null
    }
  }
}
